import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { FilterBar } from "@/components/FilterBar";
import { PropertyCard } from "@/components/PropertyCard";
import { MapView } from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SEARCH } from "@/constants/testIds";
import { Map, LayoutGrid, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [savingSearch, setSavingSearch] = useState(false);

  const initial = Object.fromEntries(params.entries());

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await api.get("/listings", { params: Object.fromEntries(params.entries()) });
      setItems(data);
      setLoading(false);
    })();
  }, [params]);

  const onSearch = (f) => setParams(f);

  const saveSearch = async () => {
    if (!user) {
      toast.error("Connectez-vous pour sauvegarder une recherche");
      return;
    }
    const filters = Object.fromEntries(params.entries());
    if (Object.keys(filters).length === 0) {
      toast.error("Ajoutez au moins un filtre");
      return;
    }
    const defaultName = filters.city || filters.country || "Ma recherche";
    const name = window.prompt("Nom de cette recherche sauvegardée ?", defaultName);
    if (!name) return;
    setSavingSearch(true);
    try {
      await api.post("/saved-searches", { name, filters });
      toast.success(`Recherche « ${name} » sauvegardée ✓ Vous recevrez une alerte sur nouvelles annonces.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSearch(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="mb-6">
          <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Recherche</span>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2">Annonces immobilières</h1>
        </div>

        <FilterBar initial={initial} onSearch={onSearch} />

        <div className="flex items-center justify-between mt-8 mb-6 gap-3 flex-wrap">
          <p data-testid={SEARCH.resultsCount} className="text-sm text-muted-foreground">
            {loading ? "Recherche..." : `${items.length} annonce${items.length > 1 ? "s" : ""} trouvée${items.length > 1 ? "s" : ""}`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={saveSearch}
              disabled={savingSearch}
              data-testid="search-save-btn"
              className="rounded-full h-9"
            >
              <BookmarkPlus className="w-4 h-4 me-1.5" /> Sauvegarder
            </Button>
            <div className="flex gap-1 bg-white border border-[#E5E5E5] rounded-full p-1">
            <Button
              size="sm"
              variant={view === "grid" ? "default" : "ghost"}
              onClick={() => setView("grid")}
              className={`rounded-full h-8 px-3 ${view === "grid" ? "bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]" : ""}`}
            >
              <LayoutGrid className="w-4 h-4 mr-1" /> Grille
            </Button>
            <Button
              size="sm"
              variant={view === "map" ? "default" : "ghost"}
              onClick={() => setView("map")}
              className={`rounded-full h-8 px-3 ${view === "map" ? "bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]" : ""}`}
            >
              <Map className="w-4 h-4 mr-1" /> Carte
            </Button>
            </div>
          </div>
        </div>

        {view === "grid" ? (
          <div data-testid={SEARCH.resultsGrid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((l, i) => (
              <PropertyCard key={l.id} listing={l} index={i} />
            ))}
            {!loading && items.length === 0 && (
              <div className="col-span-full text-center py-20 text-muted-foreground">
                Aucune annonce ne correspond à vos critères.
              </div>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-6">
            <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2">
              {items.map((l, i) => (
                <PropertyCard key={l.id} listing={l} index={i} />
              ))}
            </div>
            <div className="md:sticky md:top-24 self-start">
              <MapView listings={items} country={initial.country || "ALL"} height="80vh" />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
